import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { TRACK_MAP, TRACKS, GUT_CROSS_SELL_SLUGS } from "@/constants/tracks";
import { TRACK_PAGE } from "@/constants/testIds";
import { INTAKE_URL } from "@/constants/links";
import RelatedTracksGrid from "@/components/RelatedTracksGrid";
import GutCrossSellBand from "@/components/GutCrossSellBand";
import { MedicalReferralBand, SelfRouteCard } from "@/components/MedicalReferralBand";
import {
  ManifestoBand,
  TechCardsBand,
  MoatBand,
  WhoForBand,
  PlaceholderResults,
  PlaceholderPlans,
  TrackFaq,
  FinalCtaBand,
  ComplianceDisclaimer,
  ScanReadoutOverlay,
} from "@/components/TrackExtras";

// Per-track SEO title + description — wellness-safe, keyword-rich.
const TRACK_SEO = {
  "body-transformation": {
    title: "Body Transformation Program | Body Composition Coaching | Integrated Wellness",
    description: "Personalized body transformation and body-composition coaching — lose fat, build muscle, and see your progress every two weeks with smart body-scan tracking.",
    heroImage: "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/a74s2ahr_couple%201.png",
  },
  "metabolic-optimization": {
    title: "Metabolic Optimization Program | Energy & Metabolic Fitness | Integrated Wellness",
    description: "Steady energy, fewer crashes, and a metabolism that works with you — a personalized wellness program built around your wearable data.",
  },
  "longevity-optimization": {
    title: "Longevity Optimization Program | Healthy-Aging Coaching | Integrated Wellness",
    description: "Add healthy years with a personalized longevity program built on the Integrated Biological Age (IBA) 2.0 wellness estimate.",
  },
  "performance-optimization": {
    title: "Performance Optimization Program | Athletic Performance Coaching | Integrated Wellness",
    description: "Athletic performance and recovery coaching — strength, endurance, HRV, and recovery programmed around your data.",
  },
  "cognitive-optimization": {
    title: "Cognitive Optimization Program | Focus & Cognitive Wellness | Integrated Wellness",
    description: "Sharper focus, faster recall, and clearer days — a cognitive wellness program built around your habits, sleep, and nutrition.",
  },
  "life-optimization": {
    title: "Life Optimization Program | Habit & Behavior Coaching | Integrated Wellness",
    description: "Habit, behavior, and life-optimization coaching across habits, relationships, purpose, and energy.",
  },
  "digestive-microbiome": {
    title: "Digestive & Microbiome Optimization | Gut Health Program | Integrated Wellness",
    description: "Gut health and microbiome wellness program — optimize digestion, comfort, regularity, fiber, and food habits with a coached AI-built plan.",
  },
  "hormonal-vitality": {
    title: "Hormonal Wellness & Vitality Optimization | Energy & Sleep Program | Integrated Wellness",
    description: "A coached lifestyle program for natural vitality — sleep, training, stress, and nutrition support your body's hormonal balance. Hormone testing & therapy via our licensed medical team.",
  },
};

export default function TrackPage({ slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const track = TRACK_MAP[slug];

  useEffect(() => {
    if (!track) return;
    const seo = TRACK_SEO[track.slug];
    if (seo) {
      document.title = seo.title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", seo.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TRACK_SEO is a module constant; only `track` is a real dep
  }, [track]);

  const otherTracks = useMemo(
    () => (track ? TRACKS.filter((t) => t.slug !== track.slug) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TRACKS is a module constant; only `track` is a real dep
    [track]
  );

  if (!track) return <Navigate to="/" replace />;

  // Prefer the futuristic scan-room image for Body Transformation; fallback to track.image
  const heroImage = TRACK_SEO[track.slug]?.heroImage || track.image;

  return (
    <div data-testid={TRACK_PAGE.root(track.slug)}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--iw-border)]">
        <div className="absolute inset-0 iw-grid-bg opacity-60" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <Link
              to="/#tracks"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-primary)] hover:text-[color:var(--iw-accent-red)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Tracks
            </Link>

            <div className="mt-6 flex items-center gap-3">
              <span className="border border-[color:var(--iw-border)] bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                {track.code}
              </span>
              {track.flagship && (
                <span className="bg-[color:var(--iw-accent-red)] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-white">
                  Flagship
                </span>
              )}
            </div>

            <h1 className="mt-5 font-display text-5xl font-bold leading-[1.04] tracking-tight text-[color:var(--iw-primary)] sm:text-6xl">
              {track.name}
            </h1>
            <p className="mt-4 font-display text-xl text-[color:var(--iw-accent-red)]">
              {track.tagline}
            </p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-[color:var(--iw-body)]">
              {track.summary}
            </p>

            {track.patentTech && (
              <div className="mt-8 max-w-xl border-l-2 border-[color:var(--iw-blue)] bg-white pl-5 pr-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="iw-pulse-dot block h-1.5 w-1.5 rounded-full bg-[color:var(--iw-blue-bright)]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-blue)]">
                    Patent · Engine {track.patentTech.code} of 07
                  </span>
                </div>
                <div className="mt-2 font-display text-lg font-semibold tracking-tight text-[color:var(--iw-primary)]">
                  Powered by {track.patentTech.name}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-[color:var(--iw-body)]">
                  {track.patentTech.blurb}
                </p>
                <a
                  href="/#matrix-engine"
                  className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-blue)] hover:text-[color:var(--iw-accent-red)]"
                >
                  See it in the Matrix Engine →
                </a>
              </div>
            )}

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={INTAKE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TRACK_PAGE.applyCta(track.slug)}
                className="iw-btn-red inline-flex items-center justify-center gap-2 px-7 py-4 font-mono text-xs uppercase tracking-[0.22em]"
              >
                Get Started <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="iw-scan-sweep relative h-[360px] w-full overflow-hidden border border-[color:var(--iw-border)] sm:h-[460px] lg:h-[560px]">
              <img
                src={heroImage}
                alt={`${track.name} program at Integrated Wellness — ${track.tagline}`}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/40 to-transparent" />
              <div className="absolute left-0 top-0 w-full p-5">
                <div className="border border-[color:var(--iw-blue)]/60 bg-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur">
                  {track.code} · {track.deepDiveLabel}
                </div>
              </div>
              {/* Tech corner brackets */}
              <span className="pointer-events-none absolute left-3 top-3 h-3 w-3 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute right-3 top-3 h-3 w-3 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute left-3 bottom-3 h-3 w-3 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <span className="pointer-events-none absolute right-3 bottom-3 h-3 w-3 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
              <ScanReadoutOverlay readouts={track.scanReadouts} />
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-12 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div className="max-w-2xl">
              <div className="iw-overline mb-3">§ Pillars</div>
              <h2 className="font-display text-4xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-5xl">
                How {track.short} <span className="text-[color:var(--iw-accent-red)]">actually works.</span>
              </h2>
            </div>
            <p className="max-w-md text-base text-[color:var(--iw-body)]">
              Every pillar is measurable. Every protocol is owned by your program lead.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)] sm:grid-cols-2 lg:grid-cols-4">
            {track.pillars.map((p, i) => (
              <div key={p.title} className="bg-[color:var(--iw-surface)] p-7">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                  0{i + 1}
                </span>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-[color:var(--iw-primary)]">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--iw-body)]">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-surface)] py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="iw-overline mb-3">§ Outcomes</div>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-5xl">
              What changes, <span className="text-[color:var(--iw-accent-red)]">and how we measure it.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-[color:var(--iw-border)] border-y border-[color:var(--iw-border)]">
              {track.outcomes.map((o, i) => (
                <li
                  key={o}
                  className="flex items-baseline gap-5 py-5"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-lg font-medium text-[color:var(--iw-primary)] sm:text-xl">
                    {o}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Manifesto (digestive only — present when track.manifesto is defined) */}
      <ManifestoBand manifesto={track.manifesto} />

      {/* The Tech cards (digestive only) */}
      <TechCardsBand techCards={track.techCards} />

      {/* Moat band (digestive only) */}
      {track.techCards && <MoatBand />}

      {/* Who it's for (digestive/vitality only) */}
      <WhoForBand whoForCards={track.whoForCards} />

      {/* Medical-partner band (vitality / future tracks with medical handoff) */}
      <MedicalReferralBand medicalPartner={track.medicalPartner} />

      {/* Placeholder results + plans (digestive/vitality only) */}
      {track.whoForCards && <PlaceholderResults />}
      {track.whoForCards && <PlaceholderPlans />}

      {/* FAQ (digestive only) */}
      <TrackFaq faq={track.faq} referralNote={track.referralNote} />

      {/* Final CTA band (digestive only) */}
      <FinalCtaBand finalCta={track.finalCta} />

      {/* Compliance disclaimer (digestive only) */}
      {track.finalCta && <ComplianceDisclaimer />}

      {/* Gut cross-sell band on Body / Metabolic / Longevity / Cognitive */}
      {GUT_CROSS_SELL_SLUGS.includes(track.slug) && <GutCrossSellBand compact />}

      {/* Other tracks */}
      <RelatedTracksGrid tracks={otherTracks} />

      {/* Self-route nudge (vitality — sits ABOVE booking) */}
      <SelfRouteCard selfRouteCard={track.selfRouteCard} />
    </div>
  );
}
